/**
 * PendingBusinessSetupBanner Styles
 * SESSION_338: Portal access for pending business claims
 *
 * Distinctive teal/cyan styling for business setup in progress banner.
 * Design direction: Clean, geometric, professional with subtle depth.
 */

import { css, keyframes } from '@emotion/react';

/**
 * Subtle pulse animation for the progress indicator
 */
export const pulseKeyframes = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
`;

/**
 * Shimmer animation for progress bar
 */
export const shimmerKeyframes = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

/**
 * Slide in animation for banner entrance
 */
export const slideInKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Rotating animation for the loader icon
 */
export const spinKeyframes = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

/**
 * Base banner container styles
 */
export const bannerContainerStyles = css`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  animation: ${slideInKeyframes} 0.4s ease-out;
`;

/**
 * Setup in progress variant styles - teal/cyan gradient
 * Distinct from pending (blue/purple) and payment (amber/gold)
 */
export const setupBannerStyles = css`
  background: linear-gradient(135deg, #0891B2 0%, #06B6D4 50%, #22D3EE 100%);
  background-size: 200% 200%;
  box-shadow:
    0 4px 20px rgba(8, 145, 178, 0.25),
    0 2px 8px rgba(8, 145, 178, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
`;

/**
 * Inner content wrapper with subtle overlay
 */
export const contentWrapperStyles = css`
  position: relative;
  z-index: 1;
  padding: 20px 24px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
    z-index: -1;
  }
`;

/**
 * Geometric accent shape in background
 */
export const geometricAccentStyles = css`
  position: absolute;
  top: -20px;
  right: -20px;
  width: 140px;
  height: 140px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 50%;
  z-index: 0;
`;

/**
 * Secondary geometric accent
 */
export const geometricAccentSecondaryStyles = css`
  position: absolute;
  bottom: -40px;
  right: 60px;
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 50%;
  z-index: 0;
`;

/**
 * Third geometric accent for visual interest
 */
export const geometricAccentTertiaryStyles = css`
  position: absolute;
  top: 50%;
  left: -30px;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 50%;
  z-index: 0;
  transform: translateY(-50%);
`;

/**
 * Icon container with subtle glow
 */
export const iconContainerStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  flex-shrink: 0;
`;

/**
 * Setup icon with spin animation
 */
export const setupIconStyles = css`
  animation: ${spinKeyframes} 2s linear infinite;
`;

/**
 * Title text styles
 */
export const titleStyles = css`
  font-family: 'Comfortaa', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

/**
 * Message text styles
 */
export const messageStyles = css`
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.5;
`;

/**
 * Business name highlight styles
 */
export const businessNameStyles = css`
  font-weight: 600;
  color: #ffffff;
`;

/**
 * Progress bar container for setup state
 */
export const progressBarContainerStyles = css`
  position: relative;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 16px;
`;

/**
 * Animated shimmer overlay for progress bar
 */
export const progressBarShimmerStyles = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 100%
  );
  animation: ${shimmerKeyframes} 2s ease-in-out infinite;
`;

/**
 * Expected timeframe text styles
 */
export const timeframeStyles = css`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 12px 0 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * Feature badges container
 */
export const featureBadgesStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

/**
 * Individual feature badge
 */
export const featureBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`;
